/**
 * AiStructuredMessage - schema-driven renderer for AI structured response payloads.
 *
 * Renders an AiStructuredPayload without any per-agent or per-field business logic.
 * The component is purely presentational: it maps sections and actions to DOM
 * elements and emits AiMessageActionEvent via the onAction callback.
 *
 * Designed to be called from ChatRunView when run.structuredPayload is present.
 */

import type {
  AiMessageActionEvent,
  AiMessageActionHandler,
  AiStructuredAction,
  AiStructuredCardActionState,
  AiStructuredCardSettledLabels,
  AiStructuredItem,
  AiStructuredPayload,
  AiStructuredSection,
} from '@protoss/shared';
import { isNavigationActionPayload } from '@protoss/shared';
import { ChevronDownIcon, Collapsible, ExternalLinkIcon, IconSize, Pill } from '@protoss/ui';
import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ConfidenceBadge } from '../../../components/shared/sales/ConfidenceBadge';
import { SourceQuoteLine } from '../../../components/shared/sales/SourceQuoteLine';
import { useAiChatActionRef } from '../AiChatActionContext';
import { useChatNavigationRef } from '../navigation/ChatNavigationContext';
import { AssetDocPreview } from './AssetDocPreview';

// ─────────────────────────────────────────────────────────────────────────────
// Value formatting helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatCurrency(value: unknown): string {
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(value);
  }
  if (typeof value === 'object' && value !== null && 'amount' in value) {
    const v = value as Record<string, unknown>;
    const amount = parseFloat(String(v.amount ?? '').replace(/[^0-9.]/g, ''));
    const currency = typeof v.currency === 'string' && v.currency ? v.currency : 'USD';
    if (!isNaN(amount)) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(amount);
    }
    return String(v.amount ?? '-');
  }
  if (typeof value === 'string') {
    const amount = parseFloat(value.replace(/[^0-9.]/g, ''));
    if (!isNaN(amount) && value.trim() !== '') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        notation: 'compact',
        maximumFractionDigits: 1,
      }).format(amount);
    }
    return value;
  }
  return String(value ?? '-');
}

function formatNumber(value: unknown): string {
  if (typeof value === 'number') {
    return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(value);
  }
  return String(value ?? '-');
}

function formatDate(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    try {
      return new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(value);
    }
  }
  return String(value ?? '-');
}

// ─────────────────────────────────────────────────────────────────────────────
// Item value cell
// ─────────────────────────────────────────────────────────────────────────────

function ItemValue({ item, previous = false }: { item: AiStructuredItem; previous?: boolean }) {
  const { value, valueType = 'text' } = item;
  const isEmpty = value === null || value === undefined || value === '';

  if (isEmpty) {
    return (
      <span className="ai-structured-msg__kv-value ai-structured-msg__kv-value--empty">-</span>
    );
  }

  switch (valueType) {
    case 'url':
      return (
        <a
          className="ai-structured-msg__kv-value ai-structured-msg__kv-value--url"
          href={String(value)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {String(value)}
        </a>
      );

    case 'badge': {
      // Keep the solid pill shape for both before and after values. For the
      // existing (before) value, strike through the text via an inner span -
      // `line-through` does not paint across the pill's own inline-flex box. The
      // before pill is further dimmed by the `.kv-prev` wrapper so the new
      // value's pill stands out.
      const label = String(value);
      return (
        <Pill variant="neutral">
          {previous ? <span className="ai-structured-msg__pill-strike">{label}</span> : label}
        </Pill>
      );
    }

    case 'boolean':
      return (
        <span className="ai-structured-msg__kv-value">
          {value === true || value === 'true' ? 'Yes' : 'No'}
        </span>
      );

    case 'currency':
      return <span className="ai-structured-msg__kv-value">{formatCurrency(value)}</span>;

    case 'number':
      return <span className="ai-structured-msg__kv-value">{formatNumber(value)}</span>;

    case 'date':
      return <span className="ai-structured-msg__kv-value">{formatDate(value)}</span>;

    case 'text':
    default:
      return <span className="ai-structured-msg__kv-value">{String(value)}</span>;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Section renderers
// ─────────────────────────────────────────────────────────────────────────────

/** Compact status pills on an item's title row (e.g. a scope item's Requested/Context status). */
function ItemBadges({ badges }: { badges?: AiStructuredItem['badges'] }) {
  if (!badges?.length) return null;
  return (
    <span className="ai-structured-msg__kv-stack-badges">
      {badges.map((b, j) => (
        <Pill key={j} variant={b.variant ?? 'neutral'}>
          {b.label}
        </Pill>
      ))}
    </span>
  );
}

/** Secondary label/value detail rows under an item (e.g. a discovered question's full scope brief). */
function ItemDetails({ details }: { details?: Array<{ label: string; value: string }> }) {
  if (!details?.length) return null;
  return (
    <dl className="ai-structured-msg__kv-details">
      {details.map((d, j) => (
        <div key={j} className="ai-structured-msg__kv-detail">
          <dt className="ai-structured-msg__kv-detail-label">{d.label}</dt>
          <dd className="ai-structured-msg__kv-detail-value">{String(d.value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function KeyValueSection({ section }: { section: AiStructuredSection }) {
  if (!section.items?.length) return null;

  return (
    <div className="ai-structured-msg__section">
      {section.title && <p className="ai-structured-msg__section-title">{section.title}</p>}
      <dl className="ai-structured-msg__kv-grid">
        {section.items.map((item, i) => (
          <div key={i} className="ai-structured-msg__kv-item">
            <dt className="ai-structured-msg__kv-label">{item.label}</dt>
            <dd className="ai-structured-msg__kv-value-wrap">
              <ItemValue item={item} />
              {item.confidence && <ConfidenceBadge level={item.confidence} />}
              <SourceQuoteLine quote={item.sourceQuote} />
              <ItemDetails details={item.details} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * Variant of KeyValueSection that renders a checkbox per item for before/after comparison.
 * Used when section.selectable is true (transcript review mode).
 */
function SelectableKeyValueSection({
  section,
  fullSection,
  checkedFieldKeys,
  onToggle,
  disabled,
}: {
  section: AiStructuredSection;
  /**
   * Untruncated section when the card is collapsed - the select-all checkbox
   * must keep toggling ALL items (including hidden ones), otherwise unchecking
   * a collapsed section would silently leave hidden fields checked.
   */
  fullSection?: AiStructuredSection;
  checkedFieldKeys: ReadonlySet<string>;
  onToggle: (fieldKey: string) => void;
  disabled: boolean;
}) {
  if (!section.items?.length) return null;

  const selectableItems = (fullSection?.items ?? section.items).filter((i) => i.fieldKey);
  const allChecked =
    selectableItems.length > 0 && selectableItems.every((i) => checkedFieldKeys.has(i.fieldKey!));
  const stacked = section.layout === 'stacked';

  function handleToggleSection() {
    if (allChecked) {
      // Deselect all items in this section
      for (const item of selectableItems) onToggle(item.fieldKey!);
    } else {
      // Select only the unchecked ones
      for (const item of selectableItems) {
        if (!checkedFieldKeys.has(item.fieldKey!)) onToggle(item.fieldKey!);
      }
    }
  }

  // Stacked layout: each item is a full-width block (checkbox + title row, then the
  // answer, then its scope-brief details as a 2-column grid). Avoids cramming a rich
  // `details` brief into a narrow value column.
  if (stacked) {
    return (
      <div className="ai-structured-msg__section ai-structured-msg__section--selectable">
        {section.title && (
          <div className="ai-structured-msg__section-header">
            <label className="ai-structured-msg__section-check">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={handleToggleSection}
                className="ai-structured-msg__checkbox"
                disabled={disabled}
              />
              <span className="ai-structured-msg__section-title">{section.title}</span>
            </label>
          </div>
        )}
        <div className="ai-structured-msg__kv-stack">
          {section.items.map((item, i) => {
            const isSelectable = !!item.fieldKey;
            const isChecked = isSelectable && checkedFieldKeys.has(item.fieldKey!);
            const hasPrev =
              item.previousValue !== null &&
              item.previousValue !== undefined &&
              item.previousValue !== '';
            return (
              <div
                key={i}
                className={`ai-structured-msg__kv-stack-item${
                  isChecked ? '' : ' ai-structured-msg__kv-stack-item--unchecked'
                }`}
              >
                <label className="ai-structured-msg__kv-stack-head">
                  {isSelectable && (
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => onToggle(item.fieldKey!)}
                      className="ai-structured-msg__checkbox"
                      aria-label={item.label}
                      disabled={disabled}
                    />
                  )}
                  <span className="ai-structured-msg__kv-stack-title">{item.label}</span>
                  <ItemBadges badges={item.badges} />
                  {item.confidence && <ConfidenceBadge level={item.confidence} />}
                </label>
                <div className="ai-structured-msg__kv-stack-body">
                  {hasPrev && (
                    <span className="ai-structured-msg__kv-prev">
                      <ItemValue item={{ ...item, value: item.previousValue }} previous />
                    </span>
                  )}
                  <div className="ai-structured-msg__kv-stack-answer">
                    <ItemValue item={item} />
                  </div>
                  <SourceQuoteLine quote={item.sourceQuote} />
                  <ItemDetails details={item.details} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="ai-structured-msg__section ai-structured-msg__section--selectable">
      {section.title && (
        <div className="ai-structured-msg__section-header">
          <label className="ai-structured-msg__section-check">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={handleToggleSection}
              className="ai-structured-msg__checkbox"
              disabled={disabled}
            />
            <span className="ai-structured-msg__section-title">{section.title}</span>
          </label>
        </div>
      )}
      <dl className="ai-structured-msg__kv-grid ai-structured-msg__kv-grid--selectable">
        {section.items.map((item, i) => {
          const isSelectable = !!item.fieldKey;
          const isChecked = isSelectable && checkedFieldKeys.has(item.fieldKey!);
          const hasPrev =
            item.previousValue !== null &&
            item.previousValue !== undefined &&
            item.previousValue !== '';
          return (
            <div
              key={i}
              className={`ai-structured-msg__kv-item ai-structured-msg__kv-item--selectable${
                isChecked ? '' : ' ai-structured-msg__kv-item--unchecked'
              }`}
            >
              <div className="ai-structured-msg__kv-check">
                {isSelectable && (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => onToggle(item.fieldKey!)}
                    className="ai-structured-msg__checkbox"
                    aria-label={item.label}
                    disabled={disabled}
                  />
                )}
              </div>
              <dt className="ai-structured-msg__kv-label">{item.label}</dt>
              <dd className="ai-structured-msg__kv-value-wrap">
                {hasPrev && (
                  <span className="ai-structured-msg__kv-prev">
                    <ItemValue item={{ ...item, value: item.previousValue }} previous />
                  </span>
                )}
                <ItemValue item={item} />
                {item.confidence && <ConfidenceBadge level={item.confidence} />}
                <SourceQuoteLine quote={item.sourceQuote} />
                <ItemDetails details={item.details} />
              </dd>
            </div>
          );
        })}
      </dl>
    </div>
  );
}

function MarkdownSection({ section }: { section: AiStructuredSection }) {
  if (!section.content) return null;
  return (
    <div className="ai-structured-msg__section ai-structured-msg__section--prose">
      {section.title && <p className="ai-structured-msg__section-title">{section.title}</p>}
      <div className="ai-structured-msg__prose">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ href, children }) => (
              <a
                className="ai-structured-msg__link"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
                <ExternalLinkIcon className="ai-structured-msg__link-icon" aria-hidden="true" />
              </a>
            ),
          }}
        >
          {section.content}
        </ReactMarkdown>
      </div>
    </div>
  );
}

function TextSection({ section }: { section: AiStructuredSection }) {
  if (!section.content) return null;
  return (
    <div className="ai-structured-msg__section ai-structured-msg__section--prose">
      {section.title && <p className="ai-structured-msg__section-title">{section.title}</p>}
      <p className="ai-structured-msg__text">{section.content}</p>
    </div>
  );
}

function AlertSection({ section }: { section: AiStructuredSection }) {
  if (!section.content) return null;
  const tone = section.tone ?? 'info';
  return (
    <div
      className={`ai-structured-msg__section ai-structured-msg__alert ai-structured-msg__alert--${tone}`}
    >
      {section.title && <p className="ai-structured-msg__alert-title">{section.title}</p>}
      <p className="ai-structured-msg__alert-content">{section.content}</p>
    </div>
  );
}

function DividerSection() {
  return <hr className="ai-structured-msg__divider" aria-hidden="true" />;
}

function JsonViewSection({ section }: { section: AiStructuredSection }) {
  const [isOpen, setIsOpen] = React.useState(false);
  if (!section.content) return null;
  return (
    <div className="ai-structured-msg__section">
      <Collapsible.Root open={isOpen} onOpenChange={setIsOpen}>
        <Collapsible.Trigger asChild>
          <button type="button" className="ai-structured-msg__json-trigger">
            <ChevronDownIcon
              width={IconSize.xs}
              height={IconSize.xs}
              aria-hidden="true"
              className="ai-structured-msg__json-chevron"
              data-expanded={isOpen ? 'true' : 'false'}
            />
            {section.title ?? 'Raw data'}
          </button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <pre className="ai-structured-msg__json-pre">{section.content}</pre>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

function DocumentPreviewSection({ section }: { section: AiStructuredSection }) {
  // Collapsed by default so the chat isn't overwhelmed; the doc is fetched + rendered
  // lazily only once expanded (active={open}).
  const [open, setOpen] = React.useState(false);
  if (!section.assetUuid && !section.content && !section.previewText) return null;
  return (
    <div className="ai-structured-msg__section">
      <Collapsible.Root open={open} onOpenChange={setOpen}>
        <Collapsible.Trigger asChild>
          <button type="button" className="ai-structured-msg__doc-trigger">
            <ChevronDownIcon
              width={IconSize.xs}
              height={IconSize.xs}
              aria-hidden="true"
              className="ai-structured-msg__doc-chevron"
              data-expanded={open ? 'true' : 'false'}
            />
            {section.title ?? 'Document preview'}
          </button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <div className="ai-structured-msg__doc-body">
            <AssetDocPreview
              assetUuid={section.assetUuid ?? null}
              active={open}
              fit
              mode={section.previewMode ?? 'full'}
              heading={section.heading}
              endHeading={section.endHeading}
              fallbackText={section.previewText}
              markdownContent={section.assetUuid ? undefined : section.content}
            />
          </div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
}

function SectionRenderer({
  section,
  fullSection,
  checkedFieldKeys,
  onToggleFieldKey,
  disabled,
}: {
  section: AiStructuredSection;
  /** Untruncated section - see SelectableKeyValueSection. */
  fullSection?: AiStructuredSection;
  checkedFieldKeys?: ReadonlySet<string>;
  onToggleFieldKey?: (fieldKey: string) => void;
  disabled?: boolean;
}) {
  switch (section.type) {
    case 'key_value_grid':
      if (section.selectable && checkedFieldKeys && onToggleFieldKey) {
        return (
          <SelectableKeyValueSection
            section={section}
            fullSection={fullSection}
            checkedFieldKeys={checkedFieldKeys}
            onToggle={onToggleFieldKey}
            disabled={disabled ?? false}
          />
        );
      }
      return <KeyValueSection section={section} />;
    case 'markdown':
      return <MarkdownSection section={section} />;
    case 'text':
      return <TextSection section={section} />;
    case 'alert':
      return <AlertSection section={section} />;
    case 'divider':
      return <DividerSection />;
    case 'json_view':
      return <JsonViewSection section={section} />;
    case 'document_preview':
      return <DocumentPreviewSection section={section} />;
    default:
      return null;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Action footer
// ─────────────────────────────────────────────────────────────────────────────

type ActionState = 'accepted' | 'dismissed';

interface ActionFooterProps {
  actions: AiStructuredAction[];
  messageId: string;
  payload: AiStructuredPayload;
  /** Controlled settled state - owned by the parent. */
  actionState: ActionState | null;
  onActionStateChange: (state: ActionState) => void;
  onAction?: AiMessageActionHandler;
  onActionSettled?: (
    actionResult: ActionState,
    selectedFieldKeys: string[],
    cardKey?: string
  ) => void;
  /** Checked field keys from selectable sections - injected into accept action payload. */
  selectedFieldKeys?: string[];
  /** Key of the owning card - set for multi-card payloads, undefined for legacy. */
  cardKey?: string;
  /** Card-specific labels for the settled footer. */
  settledLabels?: AiStructuredCardSettledLabels;
  /** 'top' = the extra copy above the body on long capture cards (bottom is the default). */
  placement?: 'top' | 'bottom';
}

function ActionFooter({
  actions,
  messageId,
  payload,
  actionState,
  onActionStateChange,
  onAction,
  onActionSettled,
  selectedFieldKeys,
  cardKey,
  settledLabels,
  placement = 'bottom',
}: ActionFooterProps) {
  const contextActionRef = useAiChatActionRef();
  const chatNavigationRef = useChatNavigationRef();

  if (!actions.length) return null;

  if (actionState === 'accepted') {
    return (
      <div className="ai-structured-msg__footer ai-structured-msg__footer--result">
        <span className="ai-structured-msg__action-result ai-structured-msg__action-result--accepted">
          {settledLabels?.accepted ?? 'Fields filled'}
        </span>
      </div>
    );
  }

  if (actionState === 'dismissed') {
    return (
      <div className="ai-structured-msg__footer ai-structured-msg__footer--result">
        <span className="ai-structured-msg__action-result ai-structured-msg__action-result--dismissed">
          {settledLabels?.dismissed ?? 'Cancelled'}
        </span>
      </div>
    );
  }

  function handleClick(action: AiStructuredAction) {
    // AI navigation suggestions route to the global navigation handler and never
    // reach page-level onAction handlers (non-terminal - the card stays active).
    if (isNavigationActionPayload(action.payload)) {
      chatNavigationRef?.current?.(action.payload, 'click');
      return;
    }
    const state: ActionState | null =
      action.actionType === 'accept'
        ? 'accepted'
        : action.actionType === 'open'
          ? null
          : 'dismissed';
    if (state !== null) {
      onActionStateChange(state);
      onActionSettled?.(state, selectedFieldKeys ?? [], cardKey);
    }
    // Use the prop handler first; fall back to the context ref so the Fill button
    // works even when the rightRailItems memo captured a stale onAction closure.
    const handler = onAction ?? contextActionRef?.current;
    if (!handler) return;
    // When this is a Fill (accept) action on a transcript review card, augment the
    // action payload with the set of selected field keys so handleChatAction knows
    // which fields to apply.
    const augmentedPayload =
      action.actionType === 'accept' && selectedFieldKeys !== undefined
        ? {
            ...(typeof action.payload === 'object' && action.payload !== null
              ? action.payload
              : {}),
            selectedFieldKeys,
          }
        : action.payload;
    const event: AiMessageActionEvent = {
      messageId,
      actionId: action.id,
      actionType: action.actionType,
      payload: augmentedPayload,
      structuredPayload: payload,
      cardKey,
    };
    handler(event);
  }

  return (
    <div
      className={`ai-structured-msg__footer${
        placement === 'top' ? ' ai-structured-msg__footer--top' : ''
      }`}
    >
      {actions.map((action) => {
        const variant =
          action.variant ?? (action.actionType === 'accept' ? 'primary' : 'secondary');
        return (
          <button
            key={action.id}
            type="button"
            className={`ai-chat-btn ai-structured-msg__action-btn ai-structured-msg__action-btn--${variant}`}
            onClick={() => handleClick(action)}
          >
            {action.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export
// ─────────────────────────────────────────────────────────────────────────────

export interface AiStructuredMessageProps {
  payload: AiStructuredPayload;
  messageId: string;
  /** Persisted action state to restore on mount (legacy single-card payloads). */
  initialActionState?: 'accepted' | 'dismissed';
  /**
   * Which field keys were accepted - used to restore checkbox state when the card
   * is rehydrated from history with initialActionState === 'accepted'.
   */
  initialCheckedFieldKeys?: string[];
  /** Persisted per-card action states to restore on mount (multi-card payloads). */
  initialCardStates?: Record<string, AiStructuredCardActionState>;
  onAction?: AiMessageActionHandler;
  /**
   * Called when the user settles a card (accept/cancel) - used to persist the
   * result to the DB. cardKey is set for multi-card payloads.
   */
  onActionSettled?: (
    actionResult: 'accepted' | 'dismissed',
    selectedFieldKeys: string[],
    cardKey?: string
  ) => void;
}

/**
 * Cards collapse to the first few key-value rows so two cards fit on one
 * screen; an expand toggle reveals the rest (display-only - UI mechanics,
 * not a workspace tunable).
 */
const COLLAPSED_ITEM_LIMIT = 5;

/** Total key-value rows across a card's grid sections. */
function countGridItems(sections: AiStructuredSection[]): number {
  return sections.reduce(
    (n, s) => n + (s.type === 'key_value_grid' ? (s.items?.length ?? 0) : 0),
    0
  );
}

/**
 * Truncate grid sections to the first `limit` rows in document order, pairing
 * each visible section with its untruncated original. Grid sections past the
 * budget are dropped entirely; non-grid sections (footnotes, alerts) always
 * pass through.
 */
function truncateSections(
  sections: AiStructuredSection[],
  limit: number
): Array<{ section: AiStructuredSection; full: AiStructuredSection }> {
  let budget = limit;
  const out: Array<{ section: AiStructuredSection; full: AiStructuredSection }> = [];
  for (const s of sections) {
    if (s.type !== 'key_value_grid') {
      out.push({ section: s, full: s });
      continue;
    }
    const items = s.items ?? [];
    if (budget <= 0) continue;
    if (items.length <= budget) {
      out.push({ section: s, full: s });
      budget -= items.length;
    } else {
      out.push({ section: { ...s, items: items.slice(0, budget) }, full: s });
      budget = 0;
    }
  }
  return out;
}

/** The renderable unit - a card from payload.cards, or the legacy top-level payload. */
interface StructuredCardData {
  /** Undefined for legacy single-card payloads (keeps legacy persistence shape). */
  key?: string;
  title?: string;
  description?: string;
  sections: AiStructuredSection[];
  actions: AiStructuredAction[];
  settledLabels?: AiStructuredCardSettledLabels;
  /** Skill-reported confidence for the whole card - renders a chip in the header. */
  confidence?: AiStructuredPayload['confidence'];
}

function StructuredCardView({
  card,
  messageId,
  payload,
  initialActionState,
  initialCheckedFieldKeys,
  onAction,
  onActionSettled,
}: {
  card: StructuredCardData;
  messageId: string;
  payload: AiStructuredPayload;
  initialActionState?: 'accepted' | 'dismissed';
  initialCheckedFieldKeys?: string[];
  onAction?: AiMessageActionHandler;
  onActionSettled?: AiStructuredMessageProps['onActionSettled'];
}) {
  const hasHeader = !!(card.title || card.description);
  const { sections, actions } = card;

  // "Bare" cards (a short message + inline open-links, no title or sections) render
  // chrome-less - no card box - so they read as a message with links rather than a
  // card. Used by the deal-generation completion ("...have been generated" + per-doc
  // links).
  const isBare =
    payload.type === 'summary_card' &&
    !card.title &&
    sections.length === 0 &&
    actions.length > 0 &&
    actions.every((a) => a.actionType === 'open');

  // Settled state is lifted here so both the action footer and the checkboxes
  // react to the same source of truth.
  const [actionState, setActionState] = React.useState<'accepted' | 'dismissed' | null>(
    () => initialActionState ?? null
  );
  const isSettled = actionState !== null;

  // Collect all selectable field keys across sections - used to initialize checkbox state.
  // Computed once from the initial payload (payload doesn't change for a given message).
  const [checkedFieldKeys, setCheckedFieldKeys] = React.useState<Set<string>>(() => {
    // When rehydrating an accepted card with persisted field keys, restore exactly those.
    if (initialActionState === 'accepted' && initialCheckedFieldKeys?.length) {
      return new Set(initialCheckedFieldKeys);
    }
    // Default: all selectable keys checked, EXCEPT items flagged defaultUnchecked (changed-existing
    // review items default to "keep"; the user opts in to replace).
    const keys = new Set<string>();
    for (const section of sections) {
      if (section.selectable) {
        for (const item of section.items ?? []) {
          if (item.fieldKey && !item.defaultUnchecked) keys.add(item.fieldKey);
        }
      }
    }
    return keys;
  });

  const hasSelectableSections = sections.some((s) => s.selectable);

  // COS-100: the "Questions to capture" card can run long, and its Capture questions / Dismiss
  // buttons sat only at the very bottom - users missed them and generated on an empty scope. Show
  // the same buttons at the top too while the card is unsettled (one shared state, so either copy
  // settles both; the settled label shows once, at the bottom). Keyed on the action id, so cards
  // already in chat history get it too.
  const showTopActions =
    !isBare && actionState === null && actions.some((a) => a.id === 'capture_questions');

  // Long cards start collapsed to the first COLLAPSED_ITEM_LIMIT rows so
  // sibling cards stay visible on one screen.
  const totalGridItems = countGridItems(sections);
  const isCollapsible = totalGridItems > COLLAPSED_ITEM_LIMIT;
  const [expanded, setExpanded] = React.useState(false);
  const visibleSections =
    isCollapsible && !expanded
      ? truncateSections(sections, COLLAPSED_ITEM_LIMIT)
      : sections.map((s) => ({ section: s, full: s }));

  function handleToggleFieldKey(fieldKey: string) {
    setCheckedFieldKeys((prev) => {
      const next = new Set(prev);
      if (next.has(fieldKey)) {
        next.delete(fieldKey);
      } else {
        next.add(fieldKey);
      }
      return next;
    });
  }

  return (
    <div
      className={`ai-structured-msg${isBare ? ' ai-structured-msg--bare' : ''}`}
      data-response-type={payload.type}
    >
      {hasHeader && (
        <div className="ai-structured-msg__header">
          <div className="ai-structured-msg__header-row">
            {card.title && <span className="ai-structured-msg__title">{card.title}</span>}
            {card.confidence && (
              <ConfidenceBadge
                level={card.confidence}
                tooltip="Confidence reported by the AI for this result"
              />
            )}
          </div>
          {card.description && <p className="ai-structured-msg__description">{card.description}</p>}
        </div>
      )}

      {showTopActions && (
        <ActionFooter
          actions={actions}
          messageId={messageId}
          payload={payload}
          actionState={actionState}
          onActionStateChange={setActionState}
          onAction={onAction}
          onActionSettled={onActionSettled}
          selectedFieldKeys={hasSelectableSections ? [...checkedFieldKeys] : undefined}
          cardKey={card.key}
          settledLabels={card.settledLabels}
          placement="top"
        />
      )}

      {sections.length > 0 && (
        <div className="ai-structured-msg__body">
          {visibleSections.map(({ section, full }, i) => (
            <SectionRenderer
              key={i}
              section={section}
              fullSection={full}
              checkedFieldKeys={hasSelectableSections ? checkedFieldKeys : undefined}
              onToggleFieldKey={hasSelectableSections ? handleToggleFieldKey : undefined}
              disabled={hasSelectableSections ? isSettled : undefined}
            />
          ))}
          {isCollapsible && (
            <button
              type="button"
              className="ai-structured-msg__expand-toggle"
              aria-expanded={expanded}
              onClick={() => setExpanded((v) => !v)}
            >
              <ChevronDownIcon
                width={IconSize.xs}
                height={IconSize.xs}
                aria-hidden="true"
                className="ai-structured-msg__expand-chevron"
                data-expanded={expanded ? 'true' : 'false'}
              />
              {expanded ? 'Show less' : `Show ${totalGridItems - COLLAPSED_ITEM_LIMIT} more`}
            </button>
          )}
        </div>
      )}

      {actions.length > 0 && (
        <ActionFooter
          actions={actions}
          messageId={messageId}
          payload={payload}
          actionState={actionState}
          onActionStateChange={setActionState}
          onAction={onAction}
          onActionSettled={onActionSettled}
          selectedFieldKeys={hasSelectableSections ? [...checkedFieldKeys] : undefined}
          cardKey={card.key}
          settledLabels={card.settledLabels}
        />
      )}
    </div>
  );
}

export function AiStructuredMessage({
  payload,
  messageId,
  initialActionState,
  initialCheckedFieldKeys,
  initialCardStates,
  onAction,
  onActionSettled,
}: AiStructuredMessageProps) {
  // Multi-card payload: each card renders independently with its own settled state.
  if (payload.cards?.length) {
    return (
      <div className="ai-structured-msg-group">
        {payload.cards.map((c) => {
          const persisted = initialCardStates?.[c.key];
          return (
            <StructuredCardView
              key={c.key}
              card={{
                key: c.key,
                title: c.title,
                description: c.description,
                sections: c.sections ?? [],
                actions: c.actions ?? [],
                settledLabels: c.settledLabels,
              }}
              messageId={messageId}
              payload={payload}
              initialActionState={persisted?.result}
              initialCheckedFieldKeys={persisted?.fieldKeys}
              onAction={onAction}
              onActionSettled={onActionSettled}
            />
          );
        })}
      </div>
    );
  }

  // Legacy single-card payload - top-level title/sections/actions.
  return (
    <StructuredCardView
      card={{
        title: payload.title,
        description: payload.description,
        sections: payload.sections ?? [],
        actions: payload.actions ?? [],
        confidence: payload.confidence,
      }}
      messageId={messageId}
      payload={payload}
      initialActionState={initialActionState}
      initialCheckedFieldKeys={initialCheckedFieldKeys}
      onAction={onAction}
      onActionSettled={onActionSettled}
    />
  );
}
